import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { Plus, Trash2, ShieldOff, Image as ImageIcon, Search, UserX, AlertOctagon, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PROOF_MAX = 3_000_000;

function readAsDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function AddForm({ onCreated }) {
  const [form, setForm] = useState({
    player_name: "",
    player_user_id: "",
    player_email: "",
    cheat_tool: "",
    details: "",
    proof_image: "",
  });
  const [accountQuery, setAccountQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const searchLinkedAccount = async (q) => {
    setAccountQuery(q);
    if (!q || q.length < 2) return setSearchResults([]);
    try {
      const { data } = await api.get("/users/search", { params: { q } });
      setSearchResults(data.slice(0, 5));
    } catch {
      setSearchResults([]);
    }
  };

  const pickUser = (u) => {
    setForm((prev) => ({
      ...prev,
      player_name: prev.player_name || u.username,
      player_user_id: u.id,
      player_email: u.email,
    }));
    setAccountQuery(`${u.username} • ${u.email}`);
    setSearchResults([]);
  };

  const clearLinkedAccount = () => {
    setForm((prev) => ({ ...prev, player_user_id: "", player_email: "" }));
    setAccountQuery("");
    setSearchResults([]);
  };

  const onProof = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > PROOF_MAX) return toast.error("الصورة كبيرة (الحد 3MB)");
    setForm({ ...form, proof_image: await readAsDataURL(f) });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.player_name || !form.cheat_tool) return toast.error("الاسم وأداة الغش إلزاميان");
    setBusy(true);
    try {
      await api.post("/blacklist", form);
      toast.success("تم إضافة اللاعب للقائمة السوداء وتطبيق الحظر");
      setForm({ player_name: "", player_user_id: "", player_email: "", cheat_tool: "", details: "", proof_image: "" });
      setAccountQuery("");
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-[#111113]/90 border border-destructive/30 rounded-2xl p-6 space-y-4 shadow-xl" data-testid="blacklist-form">
      <h2 className="font-display font-black text-xl text-white flex items-center gap-2">
        <AlertOctagon className="text-destructive shrink-0" size={22} /> إدراج لاعب في القائمة السوداء
      </h2>

      <div className="relative">
        <label className="text-xs font-bold text-white/70 mb-1.5 block">ربط حساب بالمنصة (اختياري لتطبيق الحظر التلقائي)</label>
        <div className="relative">
          <Search size={16} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            data-testid="bl-account-search"
            value={accountQuery}
            onChange={(e) => searchLinkedAccount(e.target.value)}
            placeholder="ابحث بالبريد أو اسم المستخدم..."
            className="w-full bg-black/40 border border-white/10 rounded-xl pr-10 pl-4 py-2.5 outline-none focus:border-destructive/50 text-sm text-white"
          />
        </div>

        {form.player_user_id && (
          <div className="mt-2.5 flex items-center justify-between gap-2 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-3.5 py-2 text-xs text-emerald-300 font-bold">
            <div className="truncate flex items-center gap-1.5">
              <CheckCircle2 size={14} /> تم ربط الحساب: {form.player_email || form.player_user_id}
            </div>
            <button type="button" onClick={clearLinkedAccount} className="text-white/60 hover:text-white underline">إلغاء الربط</button>
          </div>
        )}

        {searchResults.length > 0 && (
          <div className="absolute z-20 top-full mt-1.5 right-0 left-0 bg-[#141417] border border-white/15 rounded-xl shadow-2xl overflow-hidden divide-y divide-white/5">
            {searchResults.map((u) => (
              <button type="button" key={u.id} onClick={() => pickUser(u)} className="w-full text-right p-3 hover:bg-white/5 flex items-center justify-between gap-2 text-xs text-white transition">
                <span className="font-bold">{u.username}</span>
                <span className="text-white/40 font-mono">{u.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <label className="text-xs font-bold text-white/70 mb-1.5 block">اسم اللاعب الغشاش</label>
        <input
          data-testid="bl-player-name"
          required
          value={form.player_name}
          onChange={(e) => setForm((prev) => ({ ...prev, player_name: e.target.value }))}
          placeholder="اسم اللاعب (يدوي أو من الحساب المرتبط)"
          className="w-full bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 outline-none focus:border-destructive/50 text-sm text-white"
        />
      </div>

      <div>
        <label className="text-xs font-bold text-white/70 mb-1.5 block">أداة / برنامج الغش المكتشف</label>
        <input
          data-testid="bl-cheat-tool"
          required
          value={form.cheat_tool}
          onChange={(e) => setForm({ ...form, cheat_tool: e.target.value })}
          placeholder="Aimbot, Wallhack, Cronus Zen, Radar..."
          className="w-full bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 outline-none focus:border-destructive/50 text-sm text-white font-mono"
        />
      </div>

      <div>
        <label className="text-xs font-bold text-white/70 mb-1.5 block">تفاصيل الواقعة أو الملاحظات</label>
        <textarea
          data-testid="bl-details"
          value={form.details}
          onChange={(e) => setForm({ ...form, details: e.target.value })}
          rows={3}
          placeholder="شرح مختصر للواقعة، رقم البطولة/المباراة، أو أدلة إضافية..."
          className="w-full bg-black/40 border border-white/10 rounded-xl px-3.5 py-2.5 outline-none focus:border-destructive/50 text-sm text-white resize-none"
        />
      </div>

      <div>
        <label className="text-xs font-bold text-white/70 mb-1.5 block">إثبات الغش (صورة — ≤3MB)</label>
        <div className="flex items-center gap-3">
          <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-black/40 border border-white/10 hover:border-destructive/50 text-xs font-bold text-white transition">
            <ImageIcon size={15} />
            <span>{form.proof_image ? "تم اختيار الصورة ✓" : "اختر صورة الإثبات"}</span>
            <input data-testid="bl-proof-input" type="file" accept="image/*" onChange={onProof} className="hidden" />
          </label>
          {form.proof_image && (
            <button type="button" onClick={() => setForm({ ...form, proof_image: "" })} className="text-xs text-destructive hover:underline">
              حذف الصورة
            </button>
          )}
        </div>
        {form.proof_image && (
          <img src={form.proof_image} alt="proof" className="mt-3 max-h-40 rounded-xl border border-white/10 object-cover" />
        )}
      </div>

      <button
        data-testid="bl-submit"
        disabled={busy}
        type="submit"
        className="w-full py-2.5 rounded-xl bg-destructive text-white font-black hover:bg-destructive/90 disabled:opacity-50 text-xs transition shadow-md mt-2"
      >
        {busy ? "جاري الإضافة وتطبيق الحظر..." : "حظر وإضافة للقائمة السوداء"}
      </button>
    </form>
  );
}

export default function BlacklistPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/blacklist");
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!confirm("هل أنت تأكد من رفع الحذف والسجل من القائمة السوداء؟")) return;
    try {
      await api.delete(`/blacklist/${id}`);
      toast.success("تم إزالة السجل من القائمة السوداء");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const isStaff = user && (user.role === "admin" || user.role === "owner");

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-destructive/10 border border-destructive/30 text-destructive text-xs font-bold mb-2">
          <ShieldOff size={13} /> الحماية والأمان
        </div>
        <h1 className="font-display font-black text-3xl md:text-4xl text-white flex items-center gap-3">
          القائمة السوداء (Blacklist)
        </h1>
        <p className="text-white/50 mt-1 text-sm">
          سجل علني للاعبين المطرودين والمحظورين لاستخدامهم أدوات غير مشروعة. الإضافة والحذف متاحان للإدارة فقط.
        </p>
      </div>

      {isStaff ? (
        <div className="grid lg:grid-cols-2 gap-6">
          <AddForm onCreated={load} />
          <section>
            <h2 className="font-display font-black text-xl text-white mb-3 flex items-center gap-2">
              <UserX className="text-destructive" size={20} /> السجلات المحظورة ({items.length})
            </h2>
            <BlacklistEntries items={items} canRemove onRemove={remove} />
          </section>
        </div>
      ) : (
        <PublicList items={items} />
      )}
    </div>
  );
}

function PublicList({ items }) {
  return (
    <section>
      <h2 className="font-display font-black text-xl text-white mb-3 flex items-center gap-2">
        <UserX className="text-destructive" size={20} /> قائمة المحظورين ({items.length})
      </h2>
      <BlacklistEntries items={items} canRemove={false} />
    </section>
  );
}

function BlacklistEntries({ items, canRemove, onRemove }) {
  return (
    <div className="space-y-3 max-h-[700px] overflow-y-auto pl-1">
      {items.length === 0 && (
        <div className="bg-[#111113]/90 border border-white/10 rounded-2xl p-8 text-center text-white/40 text-xs">
          لا توجد سجلات في القائمة السوداء حالياً
        </div>
      )}
      {items.map((b) => (
        <article
          key={b.id}
          data-testid={`blacklist-item-${b.id}`}
          className="bg-[#111113]/90 border border-destructive/30 rounded-2xl p-4 space-y-3 shadow-xl relative transition-all hover:border-destructive/50"
        >
          <div className="flex items-start justify-between gap-2 flex-wrap border-b border-white/5 pb-3">
            <div>
              <span className="inline-block text-[10px] uppercase font-black px-2 py-0.5 rounded bg-destructive/15 text-destructive border border-destructive/30 mb-1">
                محظور / Banned
              </span>
              <div className="font-display font-black text-lg text-white">{b.player_name}</div>
              {b.player_account?.email && (
                <div className="text-xs text-white/50 font-mono">{b.player_account.email}</div>
              )}
            </div>
            {canRemove && (
              <button
                data-testid={`bl-delete-${b.id}`}
                onClick={() => onRemove(b.id)}
                className="p-2 rounded-xl text-destructive hover:bg-destructive/10 transition"
                title="إزالة الحظر السجل"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>

          <div className="text-xs">
            <span className="text-white/40 font-bold">أداة الغش المكتشفة: </span>
            <span className="font-mono font-black text-destructive bg-destructive/10 border border-destructive/20 px-2 py-0.5 rounded text-xs">
              {b.cheat_tool}
            </span>
          </div>

          {b.player_account && (
            <div className="text-xs text-white/70 bg-black/40 border border-white/5 rounded-xl p-3 grid grid-cols-2 gap-2">
              <div><span className="text-white/40 font-bold">ID: </span><span className="font-mono">{b.player_account.id}</span></div>
              {b.player_account.act && <div><span className="text-white/40 font-bold">Activision: </span><span className="font-mono">{b.player_account.act}</span></div>}
              {b.player_account.clan_id && <div><span className="text-white/40 font-bold">الكلان: </span><span>{b.player_account.clan_id}</span></div>}
              <div><span className="text-white/40 font-bold">النقاط: </span><span className="text-gold-500 font-bold">{b.player_account.points || 0} pts</span></div>
            </div>
          )}

          {b.details && (
            <div className="text-xs text-white/70 leading-relaxed bg-white/5 p-3 rounded-xl border border-white/5 whitespace-pre-wrap">
              {b.details}
            </div>
          )}

          {b.proof_image && (
            <div className="space-y-1">
              <span className="text-[10px] text-white/40 font-bold">صورة الإثبات:</span>
              <img src={b.proof_image} alt="proof" className="rounded-xl max-h-64 w-full object-cover border border-white/10" />
            </div>
          )}

          <div className="text-[10px] text-white/30 flex justify-between items-center pt-2 border-t border-white/5">
            <span>أضيف بواسطة: <strong className="text-white/60">{b.added_by_username || "الإدارة"}</strong></span>
            <span className="font-mono">{new Date(b.created_at).toLocaleString("ar")}</span>
          </div>
        </article>
      ))}
    </div>
  );
}
